import { injectable, unmanaged } from 'inversify';
import FeedbackLoader from '@mail/feedback-loader/FeedbackLoader';
import EventsLogger from '@mail/feedback-loader/EventsLogger';
import merge from 'lodash/merge';

import { HttpService } from '../http';
import { Logger } from '../logger';
import { Calendar } from '../calendar';

import type { Csat } from './types';

type Survey = ConstructorParameters<typeof FeedbackLoader>[0][0];
type Options = ConstructorParameters<typeof FeedbackLoader>[1];

type JSONOptions = {
  surveys: Survey[];
  options?: Options;
  allowedPaths?: string[];
  activeForDays?: number;
};

@injectable()
export class CsatService implements Csat {
  private userId: string = '';
  private activeFrom = new Date();

  private eventsLogger: EventsLogger<string> | null = null;

  private feedbackLoader: FeedbackLoader | null = null;

  private isInitialized = false;

  constructor(
    @unmanaged() private readonly url: string,
    @unmanaged() private readonly http: HttpService,
    @unmanaged() private readonly logger: Logger,
    @unmanaged() private readonly calendar: Calendar,
    @unmanaged() private readonly checkBeforeUpdate: () => Promise<void>,
  ) {}

  setUser({ id, activeFrom }: { id: string; activeFrom?: Date }) {
    this.userId = id;

    if (activeFrom) {
      this.activeFrom = activeFrom;
    }

    this.init();
  }

  async init() {
    if (this.isInitialized) {
      this.logger.info('CSAT duplicated initialization', {
        tags: {
          vkdoc_error_type: 'csat',
        },
        context: {
          csat_error: 'duplicated initialization',
        },
      });

      return;
    }

    if (!this.url || !this.userId) {
      this.logger.info(
        'CSAT cannot be initialized because there is not url or user id',
        {
          tags: {
            vkdoc_error_type: 'csat',
          },
          context: {
            csat_error: 'no url or user id',
            url: this.url,
            userId: this.userId,
          },
        },
      );
      return;
    }

    try {
      const config = (await this.http.get(this.url, undefined, {
        withAuth: false,
      })) as JSONOptions;

      this.eventsLogger = new EventsLogger();
      this.feedbackLoader = new FeedbackLoader(
        config.surveys,
        merge(config.options || {}, {
          checkBeforeUpdate: async () => {
            if (
              !(config.allowedPaths || []).includes(window.location.pathname)
            ) {
              throw new Error('not a avalid page');
            }

            if (
              config.activeForDays &&
              typeof config.activeForDays === 'number' &&
              this.activeFrom < new Date() &&
              this.calendar.differenceInCalendarDays(
                new Date(),
                this.activeFrom,
              ) < config.activeForDays
            ) {
              throw new Error(
                `user is active for less than ${this.activeFrom} days`,
              );
            }

            return this.checkBeforeUpdate();
          },
          surveyLoaderOptions: {
            eventsLogger: this.eventsLogger,
            clientId: this.userId,
          },
        }),
      );

      this.feedbackLoader.observe();
      this.registerEvent('action:app:opened');
      this.isInitialized = true;
    } catch (error) {
      this.feedbackLoader = null;
      this.eventsLogger = null;
      this.logger.error(error, {
        tags: {
          vkdoc_error_type: 'csat',
        },
        context: {
          csat_error: error,
        },
      });
    }
  }

  stop() {
    this.feedbackLoader?.disconnect();
    this.isInitialized = false;
  }

  registerEvent(event: string) {
    this.eventsLogger?.push(event);
  }
}
